/**
 * BUG-1903 — the house money ceiling: the largest amount anyone may type, in the
 * amount's own currency. One definition for every MoneyWire door (bids, maxes,
 * sales, corrections, lot prices), so a yen house is not capped at a dollar figure.
 */
import Decimal from 'decimal.js';

import { minorUnits, type SupportedCurrency } from './currency';
import { Money } from './money';

/**
 * The ceiling in major units, before it is cut to a currency's minor units.
 *
 * It is the bound the lot price columns had as `numeric(12,2)` (BL-1978). BL-2239
 * widened them to `numeric(18,4)` without lifting it, so the typed ceiling is
 * unchanged, a bid can always become a lot price, and no value the lot columns
 * held is refused. It sits 10⁴× under the `numeric(18,4)` bid, hammer and lot
 * price columns and stays exact as a float for any display path still reading a
 * number. That headroom does NOT bound the per-acre extension: acreage is
 * unbounded, so amount × acreage is judged separately by
 * `perAcreHammerLimitRefusal` (BUG-1906).
 */
const CEILING_MAJOR_UNITS = new Decimal('9999999999.99');

/**
 * The largest amount accepted in `currency`, truncated (never rounded) to the
 * currency's minor units: ¥9,999,999,999 rather than a fraction of a yen, and
 * never rounded up to ¥10,000,000,000, which the rule itself refuses.
 *
 * @param currency - A supported ISO 4217 code.
 * @returns The ceiling as Money in that currency.
 * @sideEffect none — pure.
 */
export function moneyCeiling(currency: SupportedCurrency): Money {
    return Money.of(CEILING_MAJOR_UNITS.toDecimalPlaces(minorUnits(currency), Decimal.ROUND_DOWN), currency);
}

/**
 * Whether an amount is past the money ceiling of its own currency.
 *
 * @param amount - The amount to judge.
 * @returns True for ¥10,000,000,000 or $10,000,000,000; false for ¥15,000,000.
 * @sideEffect none — pure.
 */
export function exceedsMoneyCeiling(amount: Money): boolean {
    return amount.toDecimal().greaterThan(moneyCeiling(amount.currency).toDecimal());
}

/**
 * BUG-1906 — the bound of a `numeric(18,4)` money column (ADR 060 — Money Type
 * § 2.1): Postgres rounds the value to 4 places and refuses anything whose
 * absolute value is not below 10^14 (SQLSTATE 22003).
 */
const MONEY_COLUMN_BOUND = new Decimal('1e14');

/**
 * Whether a `numeric(18,4)` money column can store this amount. The per-unit
 * ceiling keeps every typed amount far inside it; a derived figure — a per-acre
 * bid extended by acreage — is what can outgrow it.
 *
 * @param amount - The figure the column would store.
 * @returns True when it stores; false when Postgres would raise 22003.
 * @sideEffect none — pure.
 */
export function fitsMoneyColumn(amount: Money): boolean {
    return amount.toDecimal().toDecimalPlaces(4, Decimal.ROUND_HALF_UP).abs().lessThan(MONEY_COLUMN_BOUND);
}

/**
 * The largest amount a `numeric(18,4)` money column stores in `currency`, on
 * the currency's minor-unit grid: ¥99,999,999,999,999 or $99,999,999,999,999.99.
 *
 * @param currency - A supported ISO 4217 code.
 * @returns That amount as Money.
 * @sideEffect none — pure.
 */
export function largestStorableMoney(currency: SupportedCurrency): Money {
    const step = new Decimal(10).pow(-minorUnits(currency));
    return Money.of(MONEY_COLUMN_BOUND.minus(step), currency);
}
